import { Space } from "antd";
import { FC, useEffect, useState } from "react";
import Box from "../../../components/Box";
import Container from "../../../components/Container";
import Button from "../../../components/UI/Button";
import RangePicker, {
  RangePickerValues,
} from "../../../components/UI/RangePicker";
import SelectInput from "../../../components/UI/SelectInput";
import { ISelectOption } from "../../../components/UI/SelectInput/types";
import SmartSearch from "../../../components/UI/SmartSearch";
import { useLoading } from "../../../context/LoadingProvider/useLoading";
import { SearchOptionEnum } from "../../../enums/searchOptionEnum";
import useSmartSearchParams from "../../../hooks/useSmartSearchParams";
import {
  GetBingoProgressiveContributionParams,
  getBingoProgressiveContribution,
  getProgressiveContributions,
} from "../../../services/reports";
import { createIdFromSmartSearchParams } from "../../../utils/smartSearch";
import dayjs from "dayjs";

const BingoProgressiveContribution: FC = () => {
  const loading = useLoading();
  const [search, setSearch] = useState<string>("");
  const [groupBy, setGroupBy] = useState<string>("Charity");
  const [progressive, setProgressive] = useState<string>("All");
  const [progressives, setProgressives] = useState<ISelectOption[]>([]);
  const [validSearch, setValidSearch] = useState<boolean>();
  const [params, setParams] = useSmartSearchParams();

  const TODAY = dayjs();
  const LAST_WEEK = dayjs().subtract(1, "week");

  const [rangeDate, setRangeDate] = useState<RangePickerValues>([
    TODAY,
    LAST_WEEK,
  ]);

  const refreshReport = async () => {
    loading.addLoading();

    const today = rangeDate[0];
    const lastWeek = rangeDate[1];

    const getBingoProgressiveContributionParams: GetBingoProgressiveContributionParams =
      {
        beginDate: lastWeek.format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
        groupByCompanySelect: groupBy,
        jackpotTemplateSelect: progressive,
      };

    if (params.id) {
      const id = createIdFromSmartSearchParams(params);
      getBingoProgressiveContributionParams[id] = params.id;
    }

    await getBingoProgressiveContribution(
      getBingoProgressiveContributionParams
    );

    loading.removeLoading();
  };

  useEffect(() => {
    const initialize = async () => {
      const progressiveContributions = await getProgressiveContributions();

      if (progressiveContributions) {
        const options = progressiveContributions?.map<ISelectOption>(
          (progressive) => ({
            name: progressive.text,
            value: progressive.value,
          })
        );

        setProgressives(options);
      }
    };

    initialize();
  }, []);

  return (
    <Container>
      <Box backgroundColor="white" padding="1.1em 1.8em">
        <Space direction="horizontal" size="small" wrap>
          <SmartSearch
            autoFocus
            label="Smart Search"
            status="active,closed"
            searchBy={[
              SearchOptionEnum.DISTRIBUTOR,
              SearchOptionEnum.CHARITY,
              SearchOptionEnum.LOCATION,
            ]}
            onChange={(e: string) => setSearch(e)}
            value={search}
            onObjValueChange={(e: any) => setParams({ ...params, ...e })}
            validOnEmpty
            onValidSearch={setValidSearch}
          />

          <RangePicker
            defaultValue={[LAST_WEEK, TODAY]}
            onChange={(e: RangePickerValues) => setRangeDate(e)}
            allowClear={false}
          />

          <SelectInput
            label="Group by"
            options={[
              {
                name: "Charity",
                value: "Charity",
              },
              {
                name: "Location",
                value: "Location",
              },
              {
                name: "Distributor",
                value: "Distributor",
              },
            ]}
            value={groupBy}
            onSelectChange={(e) => {setGroupBy(e?.value ? e.value : e)}}
          />

          <SelectInput
            label="Progressives"
            options={progressives}
            value={progressive}
            onSelectChange={(e) => {setProgressive(e?.value ? e.value : e)}}
          />

          <Button type="primary" onClick={() => refreshReport()} disabled={!validSearch}>
            Save as PDF
          </Button>
        </Space>
      </Box>
    </Container>
  );
};

export default BingoProgressiveContribution;
